import React from 'react';
import { FileText, Edit, Trash2, Copy } from 'lucide-react';
import Pagination from './Pagination';

const TemplateList = ({ templates, onEdit, onDelete, onUse, loading, pagination, onPageChange }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF6B00]"></div>
      </div>
    );
  }

  if (!templates || templates.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 text-center py-12 px-6">
        <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500 mb-2 font-medium">Aucun modèle de notification</p>
        <p className="text-sm text-gray-400">
          Créez des modèles pour réutiliser facilement vos notifications
        </p>
      </div>
    );
  }

  const getTypeBadge = (type) => {
    const badges = {
      INFO: { bg: 'bg-blue-100', text: 'text-blue-800', icon: '📢' },
      AVERTISSEMENT: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: '⚠️' },
      URGENCE: { bg: 'bg-red-100', text: 'text-red-800', icon: '🚨' },
      FELICITATION: { bg: 'bg-green-100', text: 'text-green-800', icon: '🎉' },
      RAPPEL: { bg: 'bg-purple-100', text: 'text-purple-800', icon: '⏰' },
      EVENEMENT: { bg: 'bg-indigo-100', text: 'text-indigo-800', icon: '📅' }
    };
    return badges[type] || badges.INFO;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => {
            const typeBadge = getTypeBadge(template.type_notification);
            
            return (
              <div
                key={template.id}
                className="group bg-white rounded-xl border border-gray-200 hover:border-[#FF6B00]/30 hover:shadow-xl transition-all duration-300 overflow-hidden"
              >
                {/* Barre de couleur selon type */}
                <div className={`h-1.5 ${typeBadge.bg}`} />

                <div className="p-5">
                  {/* En-tête */}
                  <div className="flex items-start justify-between mb-3">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold ${typeBadge.bg} ${typeBadge.text} shadow-sm`}>
                      <span className="text-base">{typeBadge.icon}</span>
                      {template.type_notification}
                    </span>
                    {template.est_actif && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 rounded-lg text-xs font-semibold shadow-sm">
                        <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                        Actif
                      </span>
                    )}
                  </div>

                  {/* Nom */}
                  <h3 className="font-bold text-lg text-gray-900 mb-2 truncate group-hover:text-[#FF6B00] transition-colors">
                    {template.nom}
                  </h3>

                  {/* Message preview */}
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <p className="text-sm text-gray-700 line-clamp-3 leading-relaxed">
                      {template.message}
                    </p>
                  </div>

                  {/* Variables */}
                  {template.variables && template.variables.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Variables disponibles</p>
                      <div className="flex flex-wrap gap-1.5">
                        {template.variables.slice(0, 3).map((variable, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2 py-1 bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 rounded-md text-xs font-mono font-semibold border border-purple-200"
                          >
                            {`{${variable}}`}
                          </span>
                        ))}
                        {template.variables.length > 3 && (
                          <span className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-600 rounded-md text-xs font-medium">
                            +{template.variables.length - 3}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Statistiques */}
                  <div className="flex items-center gap-2 mb-4 p-2.5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">
                    <span className="text-2xl">📊</span>
                    <div>
                      <p className="text-xs text-gray-600">Utilisations</p>
                      <p className="text-lg font-bold text-indigo-600">{template.nombre_utilisations || 0}</p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => onUse(template)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#FF6B00] to-[#ff8534] text-white rounded-lg hover:from-[#e55d00] hover:to-[#FF6B00] transition-all duration-200 text-sm font-semibold shadow-md hover:shadow-lg"
                    >
                      <Copy className="w-4 h-4" />
                      Utiliser
                    </button>
                    <button
                      onClick={() => onEdit(template)}
                      className="p-2.5 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 rounded-lg hover:from-gray-200 hover:to-gray-300 transition-all duration-200 shadow-sm"
                      title="Modifier"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onDelete(template.id)}
                      className="p-2.5 bg-gradient-to-r from-red-50 to-red-100 text-red-600 rounded-lg hover:from-red-100 hover:to-red-200 transition-all duration-200 shadow-sm"
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {pagination && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalItems={pagination.totalItems}
          itemsPerPage={pagination.itemsPerPage}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
};

export default TemplateList;
